/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState } from "react";
import { FormControlLabel, Checkbox, Divider, CardContent } from "@material-ui/core";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { SubDispositionList } from "./SubDispositionList";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import errorCodes from "../../../utility/errorCodes";
import { v2, errorToObject } from "@prftesp/cle-logger";
import { v4 as uuid } from "uuid";
import { ActionButtons } from "../../../components/Common";
import { EmptyPlaceholder } from "../../../components";

const COMPONENT_NAME = "Admin dispositionManager/components/DispositionDetails";

const getNameErrorMessage = (value) => {
	if (!value) {
		return "Disposition name is required.";
	}

	if (value.length > 50) {
		return "Disposition name cannot exceed 50 characters.";
	}
};

const validateDisposition = (disposition) => {
	if (!disposition.dispositionName || disposition.dispositionName.length > 50) {
		return false;
	}

	if (!disposition.subDispositions || !disposition.subDispositions.length) {
		return false;
	}

	for (let index = 0; index < disposition.subDispositions.length; index++) {
		const subDisposition = disposition.subDispositions[index];
		if (!subDisposition.subDispositionName || subDisposition.subDispositionName.length > 50) {
			return false;
		}
	}

	return true;
};

const newSubDispositionObject = (isDefault) => ({
	subDispositionId: uuid(),
	subDispositionName: "",
	isEnabled: true,
	isDefault,
});

export const DispositionDetails = ({
	disposition,
	removeDispositionHandler,
	saveDispositionHandler,
	cancelHandler,
	formEdited,
	setFormEdited,
}) => {
	const [editableDisposition, setEditableDisposition] = useState(null);
	const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

	React.useEffect(() => {
		if (disposition) {
			setEditableDisposition({ ...disposition });
		} else {
			setEditableDisposition(null);
		}
		setFormEdited(false);
	}, [setFormEdited, disposition]);

	const updatePropertyHandler = React.useCallback(
		(property, value) => {
			setEditableDisposition({ ...editableDisposition, [property]: value });
		},
		[editableDisposition],
	);

	const addSubDisposition = () => {
		setFormEdited(true);
		setEditableDisposition({
			...editableDisposition,
			subDispositions: [
				newSubDispositionObject(editableDisposition.subDispositions.length > 0 ? false : true),
				...editableDisposition.subDispositions,
			],
		});
	};

	const updateSubDispositions = (updatedSubDispositions) => {
		setFormEdited(true);
		setEditableDisposition({
			...editableDisposition,
			subDispositions: updatedSubDispositions,
		});
	};

	const removeSubDisposition = (subDispositionId) => {
		setFormEdited(true);
		setEditableDisposition({
			...editableDisposition,
			subDispositions: editableDisposition.subDispositions.filter((x) => x.subDispositionId !== subDispositionId),
		});
	};

	const markSubDispositionAsDefault = (subDispositionId) => {
		setFormEdited(true);
		setEditableDisposition({
			...editableDisposition,
			subDispositions: editableDisposition.subDispositions.map((x) => {
				if (x.subDispositionId === subDispositionId) {
					x.isDefault = true;
					x.isEnabled = true;
				} else {
					x.isDefault = false;
				}
				return x;
			}),
		});
	};

	const isDispositionValid = editableDisposition ? validateDisposition(editableDisposition) : true;

	return (
		<CardContent>
			{!editableDisposition ? (
				<EmptyPlaceholder message="To get started, select a disposition from the list on the left, or click the 'Add' button to create a new disposition." />
			) : (
				<>
					<div className="d-flex align-items-center justify-content-between flex-wrap">
						<h5 style={{ fontWeight: "bold", margin: 0 }}>
							{editableDisposition.new ? "Add Disposition" : "Edit Disposition"}
						</h5>
						<ActionButtons
							showCancel
							className="my-2 my-md-0"
							onCancel={cancelHandler}
							showRemove={!editableDisposition.new}
							onRemove={() => setRemoveConfirmOpen(true)}
							showSave={formEdited}
							onSave={() => saveDispositionHandler(editableDisposition)}
							disableSave={!isDispositionValid}
						/>
					</div>
					<div className="mt-4 d-flex align-items-center justify-content-between">
						<FormTextField
							autoFocus
							label={"Disposition name"}
							formEdited={formEdited}
							setEdited={setFormEdited}
							required
							errorMessage={getNameErrorMessage(editableDisposition.dispositionName)}
							startingValue={editableDisposition.dispositionName}
							setConfigValue={updatePropertyHandler}
							valueName={"dispositionName"}
							styleObject={{ minWidth: 120, flex: 1 }}
							enableDebounce
							errorCheck={(val) => !val || val.length > 50}
							inputProps={{
								"data-test-id": "disposition-name-input",
							}}
						/>
						<FormControlLabel
							value="top"
							style={{ paddingTop: 9, marginLeft: 20, marginRight: 0 }}
							control={
								<Checkbox
									size="small"
									color="secondary"
									onChange={(event) => {
										updatePropertyHandler("isEnabled", event.target.checked);
										setFormEdited(true);
									}}
									checked={editableDisposition.isEnabled}
									inputProps={{
										"data-test-id": "disposition-enable-checkbox",
									}}
								/>
							}
							label={<span style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 14 }}>Enable</span>}
						/>
					</div>
					<Divider style={{ margin: "40px 0 20px 0" }} />
					<SubDispositionList
						subDispositions={editableDisposition.subDispositions}
						addSubDispositionHandler={addSubDisposition}
						removeSubDispositionHandler={removeSubDisposition}
						updateSubDispositionsHandler={updateSubDispositions}
						markSubDispositionAsDefaultHandler={markSubDispositionAsDefault}
						formEdited={formEdited}
						setFormEdited={setFormEdited}
					/>
					<ConfirmDialog
						title={"Remove Disposition?"}
						message={"Are you sure you want to delete the disposition and all related sub-dispositions?"}
						confirmOpen={removeConfirmOpen}
						onClose={async (confirmed) => {
							try {
								if (confirmed) {
									removeDispositionHandler(editableDisposition.dispositionId);
								}
								setRemoveConfirmOpen(false);
							} catch (error) {
								v2.error(`Error while attempting to force close`, {
									additionalData: { error: errorToObject(error) },
									componentName: COMPONENT_NAME,
									logCode: errorCodes.DELETE_DISPOSITION_API_ERROR,
								});
							}
						}}
					/>
				</>
			)}
		</CardContent>
	);
};
